import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  BoltIcon,
  ArrowPathIcon,
  CalendarDaysIcon,
  ArrowTrendingUpIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowLeftIcon,
  SignalIcon,
  UserPlusIcon,
  PencilSquareIcon,
  ExclamationTriangleIcon,
  FunnelIcon,
  ClockIcon,
  ChartBarIcon,
} from '@heroicons/react/24/outline';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell, AreaChart, Area
} from 'recharts';
import Layout from '../components/layout/Layout';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { analyticsAPI } from '../services/api';
import toast from 'react-hot-toast';

// ── Period config ────────────────────────────────────────────────────────────
const PERIODS = [
  { key: 'today',     label: 'Today' },
  { key: 'yesterday', label: 'Yesterday' },
  { key: 'thisWeek',  label: 'This Week' },
  { key: 'lastWeek',  label: 'Last Week' },
  { key: 'thisMonth', label: 'This Month' },
  { key: 'lastMonth', label: 'Last Month' },
];

// ── Colors ────────────────────────────────────────────────────────────────────
const CHART_COLORS = {
  received:  '#6366f1',
  created:   '#10b981',
  updated:   '#3b82f6',
  converted: '#f59e0b',
  errors:    '#ef4444',
};

const PIE_COLORS = ['#10b981', '#3b82f6', '#f59e0b', '#ef4444'];

// ── Custom Tooltip ────────────────────────────────────────────────────────────
const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-lg p-3 text-sm">
      <p className="font-semibold text-gray-800 mb-1">{label}</p>
      {payload.map(entry => (
        <div key={entry.dataKey} className="flex items-center space-x-2">
          <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: entry.color }} />
          <span className="text-gray-600">{entry.name}:</span>
          <span className="font-semibold text-gray-900">{entry.value}</span>
        </div>
      ))}
    </div>
  );
};

// ── Stat card ─────────────────────────────────────────────────────────────────
function StatCard({ icon: Icon, label, value, sub, gradient, delay = 0 }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay }}
      className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 hover:shadow-md transition-shadow"
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">{label}</p>
          <p className="text-3xl font-bold text-gray-900">{value}</p>
          {sub && <p className="text-xs text-gray-400 mt-1">{sub}</p>}
        </div>
        <div className={`p-3 rounded-xl bg-gradient-to-br ${gradient}`}>
          <Icon className="h-6 w-6 text-white" />
        </div>
      </div>
    </motion.div>
  );
}

// ── Period card (small) ────────────────────────────────────────────────────────
function PeriodCard({ label, data, active, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`w-full text-left rounded-xl border p-3 transition-all ${
        active
          ? 'border-indigo-500 bg-indigo-50 ring-2 ring-indigo-300'
          : 'border-gray-200 bg-white hover:border-indigo-300 hover:bg-indigo-50/40'
      }`}
    >
      <p className={`text-xs font-bold uppercase tracking-wide mb-2 ${active ? 'text-indigo-700' : 'text-gray-600'}`}>{label}</p>
      <p className={`text-2xl font-extrabold ${active ? 'text-indigo-700' : 'text-gray-900'}`}>{data?.received || 0}</p>
      <p className="text-xs text-gray-400 mt-0.5">received</p>
      <div className="mt-2 grid grid-cols-2 gap-x-2 gap-y-1 text-xs">
        <span className="text-green-600 font-medium">+{data?.created || 0} created</span>
        <span className="text-blue-600 font-medium">~{data?.updated || 0} updated</span>
        <span className="text-amber-600 font-medium">✓{data?.converted || 0} conv.</span>
        {(data?.errors || 0) > 0 && <span className="text-red-500 font-medium">✗{data.errors} errors</span>}
      </div>
    </button>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────
export default function WebhookAnalytics() {
  const [report, setReport]           = useState(null);
  const [loading, setLoading]         = useState(true);
  const [activePeriod, setActivePeriod] = useState('thisMonth');

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const res = await analyticsAPI.getWebhooks();
      setReport(res.data);
    } catch {
      toast.error('Failed to load webhook analytics');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  // ── Selected period data ────────────────────────────────────────────────────
  const periodData = useMemo(() => {
    return report?.periodStats?.[activePeriod] || { received: 0, created: 0, updated: 0, converted: 0, errors: 0, success: 0 };
  }, [report, activePeriod]);

  // ── Daily activity filtered to active period ────────────────────────────────
  const filteredDailyActivity = useMemo(() => {
    const all = report?.dailyActivity || [];
    const now   = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    const todayStr     = today.toISOString().slice(0,10);
    const yesterdayStr = new Date(today.getTime() - 86400000).toISOString().slice(0,10);

    const weekStart    = new Date(today); weekStart.setDate(weekStart.getDate() - weekStart.getDay());
    const lastWeekStart= new Date(weekStart); lastWeekStart.setDate(lastWeekStart.getDate() - 7);
    const monthStart   = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const lastMonthEnd = new Date(now.getFullYear(), now.getMonth(), 1);

    switch (activePeriod) {
      case 'today':
        return all.filter(d => d.date === todayStr);
      case 'yesterday':
        return all.filter(d => d.date === yesterdayStr);
      case 'thisWeek':
        return all.filter(d => d.date >= weekStart.toISOString().slice(0,10));
      case 'lastWeek':
        return all.filter(d => d.date >= lastWeekStart.toISOString().slice(0,10) && d.date < weekStart.toISOString().slice(0,10));
      case 'thisMonth':
        return all.filter(d => d.date >= monthStart.toISOString().slice(0,10));
      case 'lastMonth':
        return all.filter(d => d.date >= lastMonthStart.toISOString().slice(0,10) && d.date < lastMonthEnd.toISOString().slice(0,10));
      default:
        return all;
    }
  }, [report, activePeriod]);

  // ── Period comparison chart data ────────────────────────────────────────────
  const periodChartData = useMemo(() => {
    if (!report?.periodStats) return [];
    return PERIODS.map(p => ({
      period: p.label,
      received:  report.periodStats[p.key]?.received  || 0,
      created:   report.periodStats[p.key]?.created   || 0,
      updated:   report.periodStats[p.key]?.updated   || 0,
      converted: report.periodStats[p.key]?.converted || 0,
    }));
  }, [report]);

  // ── Funnel data ─────────────────────────────────────────────────────────────
  const funnelData = useMemo(() => {
    const ov = report?.overview || {};
    return [
      { name: 'Received',   value: ov.totalReceived   || 0, color: CHART_COLORS.received  },
      { name: 'Successful', value: ov.successCount    || 0, color: CHART_COLORS.created   },
      { name: 'Created',    value: ov.createdCount    || 0, color: CHART_COLORS.updated   },
      { name: 'Converted',  value: ov.convertedCount  || 0, color: CHART_COLORS.converted },
    ];
  }, [report]);

  // ── Pie data for selected period ────────────────────────────────────────────
  const pieData = useMemo(() => {
    const d = periodData;
    const total = d.received || 0;
    if (!total) return [];
    return [
      { name: 'Created',   value: d.created   || 0 },
      { name: 'Updated',   value: d.updated   || 0 },
      { name: 'Converted', value: d.converted || 0 },
      { name: 'Errors',    value: d.errors    || 0 },
    ].filter(x => x.value > 0);
  }, [periodData]);

  const conversionRate = useMemo(() => {
    const ov = report?.overview || {};
    return ov.uniqueLeadCount > 0
      ? ((ov.convertedCount / ov.uniqueLeadCount) * 100).toFixed(1)
      : '0.0';
  }, [report]);

  // ── Loading state ────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-full min-h-[60vh]">
          <LoadingSpinner size="large" text="Loading webhook analytics..." />
        </div>
      </Layout>
    );
  }

  const noWebhook = !report?.webhook;

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50/40 to-blue-50">
        <div className="p-2 sm:p-3 lg:p-4 space-y-3">

          {/* ── Hero Header ─────────────────────────────────────────────── */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="bg-gradient-to-r from-indigo-600 via-purple-600 to-blue-600 rounded-2xl p-3 lg:p-4 text-white shadow-xl"
          >
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
              <div className="flex items-center space-x-4">
                <div className="p-3 bg-white/20 backdrop-blur-sm rounded-xl">
                  <ChartBarIcon className="h-8 w-8 text-white" />
                </div>
                <div>
                  <h1 className="text-2xl lg:text-3xl font-bold tracking-tight">Webhook Lead Analytics</h1>
                  <p className="text-indigo-100 text-sm mt-0.5">
                    {report?.webhook?.name || 'No webhook configured'} — track every lead from capture to conversion
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-3 flex-wrap gap-y-2">
                {report?.webhook && (
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
                    report.webhook.isActive ? 'bg-green-400/20 text-green-100 border border-green-400/40' : 'bg-red-400/20 text-red-100 border border-red-400/40'
                  }`}>
                    <span className={`w-1.5 h-1.5 rounded-full mr-1.5 ${report.webhook.isActive ? 'bg-green-300' : 'bg-red-300'}`} />
                    {report.webhook.isActive ? 'Active' : 'Inactive'}
                  </span>
                )}
                <button
                  onClick={fetchReport}
                  className="inline-flex items-center px-4 py-2 bg-white/20 hover:bg-white/30 backdrop-blur-sm border border-white/30 rounded-xl text-sm font-medium transition-colors"
                >
                  <ArrowPathIcon className="h-4 w-4 mr-1.5" />
                  Refresh
                </button>
                <Link
                  to="/integrations"
                  className="inline-flex items-center px-4 py-2 bg-white/20 hover:bg-white/30 backdrop-blur-sm border border-white/30 rounded-xl text-sm font-medium transition-colors"
                >
                  <ArrowLeftIcon className="h-4 w-4 mr-1.5" />
                  Webhook Setup
                </Link>
              </div>
            </div>
          </motion.div>

          {/* ── No Webhook State ─────────────────────────────────────────── */}
          {noWebhook ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.97 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white rounded-2xl border border-dashed border-gray-300 p-12 text-center"
            >
              <BoltIcon className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <h2 className="text-lg font-semibold text-gray-700 mb-2">No webhook configured</h2>
              <p className="text-sm text-gray-400 mb-6">Set up a webhook to start receiving leads from Facebook Lead Ads.</p>
              <Link
                to="/integrations"
                className="inline-flex items-center px-5 py-2.5 bg-indigo-600 text-white rounded-xl text-sm font-semibold hover:bg-indigo-700 transition-colors"
              >
                <BoltIcon className="h-4 w-4 mr-2" /> Configure Webhook
              </Link>
            </motion.div>
          ) : (
            <>
              {/* ── All-Time Overview Cards ───────────────────────────────── */}
              <div>
                <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">All-Time Overview</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                  <StatCard icon={SignalIcon}         label="Total Received"    value={report.overview.totalReceived}    gradient="from-indigo-500 to-purple-500" delay={0.0} />
                  <StatCard icon={UserPlusIcon}       label="Leads Created"     value={report.overview.createdCount}     gradient="from-emerald-500 to-green-500" delay={0.1} />
                  <StatCard icon={PencilSquareIcon}   label="Leads Updated"     value={report.overview.updatedCount}     gradient="from-blue-500 to-cyan-500"     delay={0.2} />
                  <StatCard icon={ArrowTrendingUpIcon} label="Converted"        value={report.overview.convertedCount}   sub={`${conversionRate}% conversion rate`} gradient="from-amber-500 to-orange-500" delay={0.3} />
                  <StatCard icon={CheckCircleIcon}    label="Successful Calls"  value={report.overview.successCount}     gradient="from-teal-500 to-emerald-500" delay={0.4} />
                  <StatCard icon={XCircleIcon}        label="Errors"            value={report.overview.errorCount}       gradient="from-rose-500 to-red-500"     delay={0.5} />
                  <StatCard icon={ClockIcon}          label="Pending Follow-ups" value={report.overview.pendingFollowupsCount} gradient="from-violet-500 to-purple-500" delay={0.6} />
                  <StatCard icon={FunnelIcon}         label="Unique Leads"      value={report.overview.uniqueLeadCount}  gradient="from-sky-500 to-blue-500"     delay={0.7} />
                </div>
              </div>

              {/* ── Period Selector + Period Cards ────────────────────────── */}
              <motion.div
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Period Breakdown</h2>
                  <div className="flex items-center space-x-1 bg-white border border-gray-200 rounded-xl p-1 shadow-sm">
                    {PERIODS.map(p => (
                      <button
                        key={p.key}
                        onClick={() => setActivePeriod(p.key)}
                        className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                          activePeriod === p.key
                            ? 'bg-indigo-600 text-white shadow-sm'
                            : 'text-gray-500 hover:text-indigo-600 hover:bg-indigo-50'
                        }`}
                      >
                        {p.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                  {PERIODS.map(p => (
                    <PeriodCard
                      key={p.key}
                      label={p.label}
                      data={report?.periodStats?.[p.key]}
                      active={activePeriod === p.key}
                      onClick={() => setActivePeriod(p.key)}
                    />
                  ))}
                </div>
              </motion.div>

              {/* ── Charts Row ───────────────────────────────────────────── */}
              <div className="grid grid-cols-1 xl:grid-cols-7 gap-6">

                {/* Period Comparison Bar Chart (wider) */}
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: 0.3 }}
                  className="xl:col-span-4 bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden"
                >
                  <div className="bg-gradient-to-r from-indigo-500 to-purple-600 px-5 py-4">
                    <h3 className="text-white font-semibold">Period Comparison</h3>
                    <p className="text-indigo-100 text-xs mt-0.5">Leads received, created, updated & converted by period</p>
                  </div>
                  <div className="p-4">
                    <ResponsiveContainer width="100%" height={280}>
                      <BarChart data={periodChartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="period" tick={{ fontSize: 11, fill: '#6b7280' }} />
                        <YAxis tick={{ fontSize: 11, fill: '#6b7280' }} />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend wrapperStyle={{ fontSize: 12 }} />
                        <Bar dataKey="received"  fill={CHART_COLORS.received}  name="Received"  radius={[3,3,0,0]} />
                        <Bar dataKey="created"   fill={CHART_COLORS.created}   name="Created"   radius={[3,3,0,0]} />
                        <Bar dataKey="updated"   fill={CHART_COLORS.updated}   name="Updated"   radius={[3,3,0,0]} />
                        <Bar dataKey="converted" fill={CHART_COLORS.converted} name="Converted" radius={[3,3,0,0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </motion.div>

                {/* Pie breakdown for selected period */}
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: 0.35 }}
                  className="xl:col-span-3 bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden"
                >
                  <div className="bg-gradient-to-r from-emerald-500 to-teal-500 px-5 py-4">
                    <h3 className="text-white font-semibold">
                      {PERIODS.find(p => p.key === activePeriod)?.label} — Action Mix
                    </h3>
                    <p className="text-emerald-100 text-xs mt-0.5">Breakdown of what happened with received leads</p>
                  </div>
                  <div className="p-4">
                    {pieData.length > 0 ? (
                      <>
                        <ResponsiveContainer width="100%" height={160}>
                          <PieChart>
                            <Pie
                              data={pieData}
                              cx="50%"
                              cy="50%"
                              innerRadius={50}
                              outerRadius={80}
                              paddingAngle={3}
                              dataKey="value"
                            >
                              {pieData.map((entry, index) => (
                                <Cell key={entry.name} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                              ))}
                            </Pie>
                            <Tooltip formatter={(value, name) => [value, name]} />
                          </PieChart>
                        </ResponsiveContainer>
                        <div className="grid grid-cols-2 gap-2 mt-1">
                          {pieData.map((entry, index) => (
                            <div key={entry.name} className="flex items-center space-x-2 text-sm">
                              <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: PIE_COLORS[index % PIE_COLORS.length] }} />
                              <span className="text-gray-600 truncate">{entry.name}</span>
                              <span className="font-semibold text-gray-900 ml-auto">{entry.value}</span>
                            </div>
                          ))}
                        </div>
                      </>
                    ) : (
                      <div className="h-56 flex items-center justify-center">
                        <div className="text-center">
                          <CalendarDaysIcon className="h-10 w-10 text-gray-200 mx-auto mb-2" />
                          <p className="text-sm text-gray-400">No data for this period</p>
                        </div>
                      </div>
                    )}
                  </div>
                </motion.div>
              </div>

              {/* ── Daily Activity Trend ──────────────────────────────────── */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.4 }}
                className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden"
              >
                <div className="bg-gradient-to-r from-sky-500 to-blue-600 px-5 py-4 flex items-center justify-between">
                  <div>
                    <h3 className="text-white font-semibold">Daily Activity Trend</h3>
                    <p className="text-sky-100 text-xs mt-0.5">
                      {PERIODS.find(p => p.key === activePeriod)?.label} — received vs created vs converted per day
                    </p>
                  </div>
                  <span className="text-sky-100 text-xs bg-white/20 rounded-lg px-2 py-1">
                    {filteredDailyActivity.length} day{filteredDailyActivity.length !== 1 ? 's' : ''}
                  </span>
                </div>
                <div className="p-4">
                  {filteredDailyActivity.length > 0 ? (
                    <ResponsiveContainer width="100%" height={220}>
                      <AreaChart data={filteredDailyActivity} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                        <defs>
                          <linearGradient id="gradReceived" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%"  stopColor={CHART_COLORS.received}  stopOpacity={0.15} />
                            <stop offset="95%" stopColor={CHART_COLORS.received}  stopOpacity={0} />
                          </linearGradient>
                          <linearGradient id="gradCreated" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%"  stopColor={CHART_COLORS.created}   stopOpacity={0.15} />
                            <stop offset="95%" stopColor={CHART_COLORS.created}   stopOpacity={0} />
                          </linearGradient>
                          <linearGradient id="gradConverted" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%"  stopColor={CHART_COLORS.converted} stopOpacity={0.15} />
                            <stop offset="95%" stopColor={CHART_COLORS.converted} stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#6b7280' }} />
                        <YAxis tick={{ fontSize: 11, fill: '#6b7280' }} />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend wrapperStyle={{ fontSize: 12 }} />
                        <Area type="monotone" dataKey="received"  stroke={CHART_COLORS.received}  fill="url(#gradReceived)"  strokeWidth={2} name="Received"  dot={{ r: 3 }} />
                        <Area type="monotone" dataKey="created"   stroke={CHART_COLORS.created}   fill="url(#gradCreated)"   strokeWidth={2} name="Created"   dot={{ r: 3 }} />
                        <Area type="monotone" dataKey="updated"   stroke={CHART_COLORS.updated}   fill="none"                strokeWidth={2} name="Updated"   dot={{ r: 3 }} strokeDasharray="4 2" />
                        <Area type="monotone" dataKey="converted" stroke={CHART_COLORS.converted} fill="url(#gradConverted)" strokeWidth={2} name="Converted" dot={{ r: 3 }} />
                      </AreaChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-[220px] flex items-center justify-center">
                      <div className="text-center">
                        <CalendarDaysIcon className="h-10 w-10 text-gray-200 mx-auto mb-2" />
                        <p className="text-sm text-gray-400">No daily data for this period</p>
                        <p className="text-xs text-gray-300 mt-1">Leads logged during this period will appear here</p>
                      </div>
                    </div>
                  )}
                </div>
              </motion.div>

              {/* ── Conversion Funnel + Recent Conversions ────────────────── */}
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">

                {/* Funnel */}
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: 0.45 }}
                  className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden"
                >
                  <div className="bg-gradient-to-r from-amber-500 to-orange-500 px-5 py-4">
                    <h3 className="text-white font-semibold">All-Time Conversion Funnel</h3>
                    <p className="text-amber-100 text-xs mt-0.5">From webhook hit to closed deal</p>
                  </div>
                  <div className="p-5 space-y-3">
                    {funnelData.map((stage, idx) => {
                      const maxVal = funnelData[0].value || 1;
                      const pct = Math.round((stage.value / maxVal) * 100);
                      return (
                        <div key={stage.name}>
                          <div className="flex items-center justify-between text-sm mb-1">
                            <div className="flex items-center space-x-2">
                              <span className="w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold text-white" style={{ background: stage.color }}>{idx + 1}</span>
                              <span className="font-medium text-gray-700">{stage.name}</span>
                            </div>
                            <span className="font-bold text-gray-900">{stage.value} <span className="text-xs text-gray-400 font-normal">({pct}%)</span></span>
                          </div>
                          <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
                            <div
                              className="h-full rounded-full transition-all duration-700"
                              style={{ width: `${pct}%`, background: stage.color }}
                            />
                          </div>
                        </div>
                      );
                    })}
                    <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between text-sm">
                      <span className="text-gray-500 font-medium">Overall Conversion Rate</span>
                      <span className="text-2xl font-bold text-amber-600">{conversionRate}%</span>
                    </div>
                  </div>
                </motion.div>

                {/* Recent Conversions */}
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: 0.5 }}
                  className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden"
                >
                  <div className="bg-gradient-to-r from-violet-500 to-purple-600 px-5 py-4">
                    <h3 className="text-white font-semibold">Recent Conversions</h3>
                    <p className="text-violet-100 text-xs mt-0.5">Latest webhook leads that became customers</p>
                  </div>
                  <div className="divide-y divide-gray-50">
                    {report.recentConversions?.length ? (
                      report.recentConversions.map((item, i) => (
                        <div key={item.leadId} className="flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors">
                          <div className="flex items-center space-x-3 min-w-0">
                            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-violet-400 to-purple-500 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                              {i + 1}
                            </div>
                            <div className="min-w-0">
                              <p className="text-sm font-semibold text-gray-800 truncate">{item.customerName}</p>
                              <p className="text-xs text-gray-400 font-mono">{item.leadNumber}</p>
                            </div>
                          </div>
                          <div className="text-right flex-shrink-0 ml-3">
                            <p className="text-xs font-medium text-violet-600">
                              {new Date(item.convertedAt).toLocaleDateString()}
                            </p>
                            <p className="text-xs text-gray-400">
                              {new Date(item.convertedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </p>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="px-4 py-8 text-center">
                        <ArrowTrendingUpIcon className="h-10 w-10 text-gray-200 mx-auto mb-3" />
                        <p className="text-sm text-gray-400">No conversions yet</p>
                        <p className="text-xs text-gray-300 mt-1">Converted webhook leads will appear here</p>
                      </div>
                    )}
                  </div>
                </motion.div>
              </div>

              {/* ── Daily Activity Table ──────────────────────────────────── */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.55 }}
                className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden"
              >
                <div className="bg-gradient-to-r from-slate-600 to-gray-700 px-5 py-4 flex items-center justify-between">
                  <div>
                    <h3 className="text-white font-semibold">Daily Activity Log</h3>
                    <p className="text-gray-300 text-xs mt-0.5">
                      {PERIODS.find(p => p.key === activePeriod)?.label} — day-by-day breakdown
                    </p>
                  </div>
                  <span className="text-gray-300 text-xs">{filteredDailyActivity.length} rows</span>
                </div>
                {filteredDailyActivity.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead>
                        <tr className="border-b border-gray-100 bg-gray-50">
                          <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Date</th>
                          <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wide">Received</th>
                          <th className="px-4 py-3 text-center text-xs font-semibold text-green-600 uppercase tracking-wide">Created</th>
                          <th className="px-4 py-3 text-center text-xs font-semibold text-blue-600 uppercase tracking-wide">Updated</th>
                          <th className="px-4 py-3 text-center text-xs font-semibold text-amber-600 uppercase tracking-wide">Converted</th>
                          <th className="px-5 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wide">Conv. Rate</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50">
                        {[...filteredDailyActivity].reverse().map(row => {
                          const rate = row.received > 0 ? ((row.converted / row.received) * 100).toFixed(0) : 0;
                          return (
                            <tr key={row.date} className="hover:bg-indigo-50/30 transition-colors">
                              <td className="px-3 py-2.5 text-center text-sm font-medium text-gray-600">{row.date}</td>
                              <td className="px-3 py-2.5 text-center font-semibold text-gray-900">{row.received}</td>
                              <td className="px-3 py-2.5 text-center font-semibold text-green-600">{row.created}</td>
                              <td className="px-3 py-2.5 text-center font-semibold text-blue-600">{row.updated}</td>
                              <td className="px-3 py-2.5 text-center font-semibold text-amber-600">{row.converted}</td>
                              <td className="px-3 py-2.5 text-center">
                                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                                  rate >= 20 ? 'bg-green-100 text-green-700' :
                                  rate > 0   ? 'bg-amber-100 text-amber-700' :
                                               'bg-gray-100 text-gray-500'
                                }`}>
                                  {rate}%
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="py-8 text-center">
                    <CalendarDaysIcon className="h-10 w-10 text-gray-200 mx-auto mb-3" />
                    <p className="text-sm text-gray-400">No activity data for this period</p>
                  </div>
                )}
              </motion.div>

            </>
          )}
        </div>
      </div>
    </Layout>
  );
}
